import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Wallet, PiggyBank, CreditCard } from 'lucide-react';

const PERIODS = [
    { value: '1m', label: 'Mese corrente' },
    { value: '30d', label: 'Ultimi 30 giorni' },
    { value: '6m', label: 'Ultimi 6 mesi' },
    { value: '1y', label: 'Ultimo anno' },
    { value: 'ytd', label: 'Anno in corso' },
];

export function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('6m');

    useEffect(() => {
        setLoading(true);
        api.getDashboard({ period })
            .then((d) => {
                setData(d);
                setLoading(false);
            });
    }, [period]);

    if (loading || !data) {
        return <p className="text-gray-500">Caricamento...</p>;
    }

    const chartData = data.trend.map((t) => ({
        name: t.label,
        spese: t.expense,
        entrate: t.income,
    }));

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-500 text-sm mt-1">Panoramica dei tuoi conti</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KpiCard
                    label="Saldo totale"
                    value={data.balance}
                    icon={Wallet}
                    color="indigo"
                />
                <KpiCard
                    label="Entrate mese"
                    value={data.monthly_income}
                    icon={ArrowUpRight}
                    color="emerald"
                    positive
                />
                <KpiCard
                    label="Spese mese"
                    value={data.monthly_expense}
                    icon={ArrowDownRight}
                    color="rose"
                />
                <KpiCard
                    label="Risparmio"
                    value={data.monthly_income - data.monthly_expense}
                    icon={PiggyBank}
                    color="amber"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold text-gray-900">Trend spese</h2>
                        <select
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className="px-3 py-1.5 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                        >
                            {PERIODS.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                                <Tooltip formatter={(value) => `€ ${Number(value).toFixed(2)}`} />
                                <Line type="monotone" dataKey="spese" stroke="#ef4444" strokeWidth={3} dot={{ r: 4, fill: '#ef4444' }} activeDot={{ r: 6 }} name="Spese" />
                                <Line type="monotone" dataKey="entrate" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981' }} activeDot={{ r: 6 }} name="Entrate" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h2 className="font-semibold text-gray-900 mb-4">Ultimi movimenti</h2>
                    <div className="space-y-4">
                        {data.recent_transactions.length === 0 && (
                            <p className="text-sm text-gray-500">Nessun movimento recente.</p>
                        )}
                        {data.recent_transactions.map((t) => (
                            <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                <div>
                                    <p className="text-sm font-medium text-gray-900">{t.description}</p>
                                    <p className="text-xs text-gray-500">{format(new Date(t.transaction_date), 'dd MMM yyyy', { locale: it })}</p>
                                </div>
                                <p className={`text-sm font-semibold ${t.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    {t.type === 'income' ? '+' : '-'} € {parseFloat(t.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <h2 className="font-semibold text-gray-900 mb-4">I tuoi conti</h2>
                {data.accounts_balance.length === 0 ? (
                    <p className="text-sm text-gray-500">Nessun conto.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {data.accounts_balance.map((account) => {
                            const Icon = account.type === 'cash' ? Wallet : CreditCard;
                            return (
                                <div key={account.id} className="p-4 rounded-xl border border-gray-100 bg-gray-50 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                        <Icon size={20} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{account.name}</p>
                                        <p className="text-lg font-bold text-gray-900">€ {parseFloat(account.balance).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiCard({ label, value, icon: Icon, color, positive }) {
    const colors = {
        indigo: 'bg-indigo-50 text-indigo-600',
        emerald: 'bg-emerald-50 text-emerald-600',
        rose: 'bg-rose-50 text-rose-600',
        amber: 'bg-amber-50 text-amber-600',
    };

    const isPositive = value >= 0;

    return (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{label}</p>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
                    <Icon size={20} />
                </div>
            </div>
            <p className={`text-2xl font-bold ${positive && !isPositive ? 'text-rose-600' : 'text-gray-900'}`}>
                € {Math.abs(value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </p>
        </div>
    );
}
